import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Film, ImageIcon, Sparkles, Users, Image as ImageIconSm, Video as VideoIconSm } from "lucide-react";
import { cn } from "@/lib/utils";
import { Modal, SearchInput, GridSkeleton, ButtonTabs, Skeleton } from "@/components/ui";
import { useUserStore, getDisplayUrl } from "@/stores/user-store";
import { useAssets, useCharacters, type CharacterCard } from "@/hooks";
import type { Asset, AssetSource } from "@/types";

type AssetType = "video" | "image";
type FilterType = "all" | "generation" | "characters";

interface AssetPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (asset: Asset) => void;
  assetType: AssetType;
  /** Show filter tabs for generated/characters */
  showFilters?: boolean;
  title?: string;
}

const filterTabs: { id: string; value: FilterType; label: string }[] = [
  { id: "all", value: "all", label: "All" },
  { id: "generation", value: "generation", label: "Generated" },
  { id: "characters", value: "characters", label: "Characters" },
];

export function AssetPickerModal({
  isOpen,
  onClose,
  onSelect,
  assetType,
  showFilters = false,
  title,
}: AssetPickerModalProps) {
  const navigate = useNavigate();
  const { plan } = useUserStore();
  const [filter, setFilter] = useState<FilterType>("all");
  const [search, setSearch] = useState("");

  const availableFilterTabs = useMemo(() => {
    // The Characters tab only makes sense for image pickers — videos
    // live on a character but the picker's purpose for video pickers
    // (e.g. extend-video) is selecting a previously-generated VIDEO.
    if (assetType !== "image") {
      return filterTabs.filter((tab) => tab.value !== "characters");
    }
    return filterTabs;
  }, [assetType]);

  useEffect(() => {
    if (assetType !== "image" && filter === "characters") {
      setFilter("all");
    }
  }, [filter, assetType]);

  // The standard asset query — used for All / Generated.
  // For the Characters tab we use the dedicated characters endpoint
  // instead so we get aggregated cards (one per folder) rather than
  // a flat list of every character image.
  const sourceFilter: AssetSource | undefined =
    filter === "all" || filter === "characters" ? undefined : filter;

  const { data, isLoading } = useAssets({
    perPage: 50,
    source: sourceFilter,
  });

  const { data: charactersData, isLoading: isLoadingCharacters } =
    useCharacters();

  const modalTitle =
    title || (assetType === "video" ? "Select a Video" : "Select from Assets");
  const EmptyIcon = assetType === "video" ? Film : ImageIcon;
  const emptyText =
    assetType === "video" ? "No videos found" : "No images found";

  // Filter assets by type, status, and search
  const filteredAssets = useMemo(() => {
    if (!data?.assets) return [];

    return data.assets.filter((asset) => {
      // Filter by media type
      const mediaType = assetType === "video" ? "VIDEO" : "IMAGE";
      if (asset.media_type !== mediaType) return false;

      // Only show completed/ready assets - exclude processing/pending/failed
      if (asset.status !== "completed" && asset.status !== "ready")
        return false;

      // Filter by search (prompt or title-like text)
      if (search) {
        const searchLower = search.toLowerCase();
        const promptMatch = asset.prompt?.toLowerCase().includes(searchLower);
        const idMatch = asset.id.toLowerCase().includes(searchLower);
        if (!promptMatch && !idMatch) return false;
      }

      return true;
    });
  }, [data?.assets, filter, search, assetType]);

  const handleSelect = (asset: Asset) => {
    onSelect(asset);
    onClose();
  };

  // Filter characters by search (name match). Characters with no
  // canonical image (e.g. only failed generations) are skipped — we
  // can't build a usable Asset payload without a URL + id.
  const filteredCharacters = useMemo<CharacterCard[]>(() => {
    const list = charactersData?.characters ?? [];
    return list.filter((c) => {
      if (!c.canonical_url || !c.canonical_id) return false;
      if (!search) return true;
      return c.name.toLowerCase().includes(search.toLowerCase());
    });
  }, [charactersData, search]);

  const handleSelectCharacter = (character: CharacterCard) => {
    if (!character.canonical_url || !character.canonical_id) return;
    // Synthesize an Asset payload from the character card. The picker's
    // consumers only read a small subset (id, url, source, folder, plus
    // optionally media_type / status), so the cast is safe — the
    // remaining fields are filled with sensible defaults.
    const synthesized: Asset = {
      id: character.canonical_id,
      user_id: "",
      source: "generation",
      media_type: "IMAGE",
      url: character.canonical_url,
      url_watermarked: null,
      status: "completed",
      file_size_bytes: 0,
      width: 0,
      height: 0,
      mime_type: "image/png",
      is_favorite: false,
      created_at: character.latest_at,
      folder: character.name,
    };
    handleSelect(synthesized);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={modalTitle}
      className="max-w-3xl h-[calc(100vh-4rem)] sm:h-auto"
    >
      <div className="flex flex-col h-full gap-4">
        {/* Search and Filters */}
        <div
          className={cn(
            "flex gap-3 shrink-0",
            showFilters ? "flex-col sm:flex-row" : "",
          )}
        >
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder={
              assetType === "video" ? "Search videos..." : "Search assets..."
            }
            className={showFilters ? "flex-1" : ""}
          />

          {showFilters && (
            <ButtonTabs
              items={availableFilterTabs}
              value={filter}
              onChange={setFilter}
              variant="compact"
            />
          )}
        </div>

        {/* Assets Grid */}
        <div className="flex-1 min-h-0 overflow-y-auto sm:max-h-[400px]">
          {filter === "characters" ? (
            // ── Characters tab ──────────────────────────────────────
            // One card per character; click selects the canonical
            // image as the source AND carries `folder` through so the
            // resulting animation lands under the character.
            isLoadingCharacters ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="aspect-[3/4] rounded-lg" />
                ))}
              </div>
            ) : filteredCharacters.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {filteredCharacters.map((c) => (
                  <button
                    key={c.name}
                    onClick={() => handleSelectCharacter(c)}
                    className="group relative aspect-[3/4] rounded-lg overflow-hidden border border-white/[0.08] hover:border-white/[0.2] transition-all bg-black text-left"
                  >
                    <img
                      src={c.canonical_url ?? undefined}
                      alt={c.name}
                      className="absolute inset-0 size-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <span className="px-3 py-1.5 rounded-lg bg-white text-black text-xs font-medium">
                        Select
                      </span>
                    </div>
                    <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-black/90 via-black/40 to-transparent">
                      <div className="text-sm font-medium text-white truncate">
                        {c.name}
                      </div>
                      <div className="flex items-center gap-3 mt-0.5 text-[11px] text-white/70">
                        <span className="flex items-center gap-1">
                          <ImageIconSm className="size-3" />
                          {c.image_count}
                        </span>
                        <span className="flex items-center gap-1">
                          <VideoIconSm className="size-3" />
                          {c.video_count}
                        </span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Users className="size-10 text-[#444] mb-3" />
                <p className="text-sm text-[#666]">
                  {search ? "No characters match" : "No characters yet"}
                </p>
                {!search && (
                  <div className="mt-3">
                    <p className="text-xs text-[#555]">
                      Create a character to animate them later
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        onClose();
                        navigate("/characters");
                      }}
                      className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white/[0.08] border border-white/[0.12] text-sm text-white hover:bg-white/[0.12] transition-colors"
                    >
                      <Sparkles className="size-3.5" />
                      Create a Character
                    </button>
                  </div>
                )}
              </div>
            )
          ) : isLoading ? (
            <GridSkeleton count={6} columns={3} />
          ) : filteredAssets.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {filteredAssets.map((asset) => (
                <button
                  key={asset.id}
                  onClick={() => handleSelect(asset)}
                  className="group relative aspect-[4/3] rounded-lg overflow-hidden border border-white/[0.08] hover:border-white/[0.2] transition-all bg-black"
                >
                  {asset.media_type === "VIDEO" ? (
                    <video
                      src={getDisplayUrl(asset, plan)}
                      className="w-full h-full object-contain"
                      muted
                      playsInline
                      onMouseEnter={(e) => e.currentTarget.play()}
                      onMouseLeave={(e) => {
                        e.currentTarget.pause();
                        e.currentTarget.currentTime = 0;
                      }}
                    />
                  ) : (
                    <img
                      src={getDisplayUrl(asset, plan)}
                      alt={asset.prompt || "Asset"}
                      className="w-full h-full object-contain"
                    />
                  )}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <span className="px-3 py-1.5 rounded-lg bg-white text-black text-xs font-medium">
                      Select
                    </span>
                  </div>
                  <div className="absolute bottom-0 inset-x-0 p-2 bg-gradient-to-t from-black/80 to-transparent">
                    <p className="text-xs text-white truncate">
                      {asset.prompt || `${asset.media_type} ${asset.source}`}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <EmptyIcon className="size-10 text-[#444] mb-3" />
              <p className="text-sm text-[#666]">{emptyText}</p>
              {search || filter !== "all" ? (
                <p className="text-xs text-[#555] mt-1">
                  {showFilters
                    ? "Try adjusting your search or filters"
                    : "Try adjusting your search"}
                </p>
              ) : (
                <div className="mt-3">
                  <p className="text-xs text-[#555]">
                    Generate your first {assetType === "video" ? "video" : "image"} to get started
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      navigate(assetType === "video" ? "/video/t2v" : "/images/t2i");
                    }}
                    className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white/[0.08] border border-white/[0.12] text-sm text-white hover:bg-white/[0.12] transition-colors"
                  >
                    <Sparkles className="size-3.5" />
                    {assetType === "video" ? "Create a Video" : "Create an Image"}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
